/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import { useNavigate, Link } from 'react-router-dom';
import { callAPI, getCitizenIdType, getCitizenTypeLabel, setAuthCookie, userErrorMessage, showErrorToast } from 'helpers';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import config from 'config';

// Components
import ModalTermsAndConditions from 'components/registrations/ModalTermsAndConditions';
// import ModalPDPAConsent from 'components/registrations/ModalPDPAConsent';
import NotFoundTicket from 'components/registrations/NotFoundTicket';
import FacebookLogin from 'components/profile/FacebookLogin';
import GoogleLogin from 'components/profile/GoogleLogin';

// Hooks
import usePassword from 'hooks/usePassword';
import NotFoundSubmitted from 'components/registrations/NotFoundSubmitted';
import useQuery from 'hooks/useQuery';

const Lookup = () => {
  const navigate = useNavigate();
  const { passwordHidden, onPasswordHiddenChange } = usePassword();
  const query = useQuery();
  const registrable = true;

  // const _consent = Cookies.get('consent_accepted_at');
  // const [consentAccepted, setConsentAccepted] = useState(Boolean(_consent));
  // useEffect(() => setConsentAccepted(_consent), [_consent]);

  const [lookingUp, setLookingUp] = useState(false);
  const [citizenId, setCitizenId] = useState(query.get('citizen_id') || '');
  const [citizenIdType, setCitizenIdType] = useState();
  const [password, setPassword] = useState('');
  const [lookupState, setLookupState] = useState();
  const [ticketSuccessEmail, setTicketSuccessEmail] = useState('');
  const [loginMethod, setLoginMethod] = useState('social');
  const [wrongCitizenId, setWrongCitizenId] = useState(false);
  // G29-3: ThaID callback ล้ม (แลก token ไม่ได้ / ยกเลิกที่ ThaID / ปิดระบบ) → /?state=error — เดิมไม่มีข้อความ หน้าเปล่า
  // แยกจาก lookupState เพราะ lookupState ที่ไม่ว่างถูกตีความเป็น "ค้นพบแล้ว" (ช่องเลขบัตรขึ้นเขียว / "ปิดระบบลงทะเบียน")
  const [thaidError, setThaidError] = useState(false);

  // Uncomment while counting down.
  // useEffect(() => {
  //   window.location.href = '/countdown.html';
  // }, []);

  // ข้าม reset 1 รอบตอน prefill citizenId จาก ThaiD callback — ไม่งั้น lookupState จาก query โดนล้างทิ้ง
  const prefillingRef = useRef(false);

  // spinner เต็มจอระหว่างแลก handoff code + auto-lookup — ไม่งั้นหน้า / ดูค้างเฉยๆ ทั้งที่กำลังทำงาน
  const [handingOff, setHandingOff] = useState(Boolean(query.get('handoff')));
  useEffect(() => {
    const root = document.getElementById('root');
    root.classList.toggle('is-loading', handingOff);
    return () => root.classList.remove('is-loading');
  }, [handingOff]);

  useEffect(() => {
    setCitizenIdType(getCitizenIdType(citizenId));
    if (prefillingRef.current) {
      prefillingRef.current = false;
      return;
    }
    setLookupState();
    setWrongCitizenId();
    setThaidError(false);
    setPassword('');
  }, [citizenId]);

  useEffect(() => {
    const initialState = query.get('state');
    setThaidError(initialState === 'error');
    setLookupState(initialState === 'error' ? undefined : initialState);

    const _autoLookup = async (citizenId) => {
      if (lookingUp || !citizenId) return;
      try {
        setLookingUp(true);
        const result = await callAPI({
          method: 'POST',
          url: '/applicants/student-lookup',
          body: { citizenId },
        });

        setLookupState(result.state);
        setSocialLogin(result.social_login);
        setAuthCookie('sessionToken', result.token, 1 / 24);
        if (['obec', 'obec-past', 'ticket'].includes(result.state)) {
          if (registrable) {
            navigate('/form');
          }
        } else if (result.state === 'tutor-suspected') {
          navigate(`/veto/${result.blacklist_token}`);
        }

        setLookingUp(false);
      } catch (error) {
        console.error(new Error(`Error while trying to lookup by citizenId: ${error.message}`));
        showErrorToast(userErrorMessage(error));
        setLookingUp(false);
      }
    };

    const _prefill = (value) => {
      if (value && value !== citizenId) prefillingRef.current = true;
      setCitizenId(value);
    };

    const handoffCode = query.get('handoff');
    if (handoffCode) {
      // ThaiD callback ส่ง one-time code แทน citizen_id ตรงๆ — แลกคืนแล้วล้าง query ออกจาก URL
      const _exchangeHandoff = async () => {
        try {
          const { citizen_id } = await callAPI({
            fullURL: `${config.thaid.apiHost}/thaid/handoff`,
            method: 'POST',
            body: { code: handoffCode },
          });
          window.history.replaceState(null, '', window.location.pathname);
          _prefill(citizen_id || '');
          if (query.get('next') === '1') await _autoLookup(citizen_id);
        } catch (error) {
          // code หมดอายุ/ถูกใช้ไปแล้ว — ให้กรอกเลขเอง (ข้อความ state ยังแสดงตาม query)
        }
        setHandingOff(false);
      };
      _exchangeHandoff();
      return;
    }

    // legacy: รองรับ callback ตัวเก่าที่ยังส่ง citizen_id ใน query จนกว่า backend ใหม่จะ deploy
    const citizenIdFromQuery = query.get('citizen_id') || '';
    _prefill(citizenIdFromQuery);
    if (query.get('next') === '1') _autoLookup(citizenIdFromQuery);
  }, [navigate, query]); // eslint-disable-line react-hooks/exhaustive-deps

  const citizenTypeLabel = getCitizenTypeLabel(citizenIdType);

  const _onCitizenIdChange = (e) => {
    // if (!consentAccepted) return;
    const id = e.target.value;
    if (!id.match(new RegExp('^[a-zA-Z0-9]{0,13}$', 'g'))) return;
    setCitizenId(id.toUpperCase());
    setLoginFail(false);
  };

  const _onCitizenIdEnter = (e) => {
    if (e.key === 'Enter') {
      _onLoginClick();
    }
  };

  const _onPasswordEnter = (e) => {
    if (e.key === 'Enter') {
      _onLoginClick();
    }
  };

  const _onThaidLogin = () => {
    // state ต้องเป็นค่าสุ่มเดาไม่ได้ (กัน CSRF) — ห้ามใส่เลขบัตร ปชช. (รั่วเข้า log/history ของ DOPA)
    // callback ฝั่ง server ใช้ pid ที่ ThaiD verify แล้วเป็น source of truth ไม่ได้อ่าน state
    const state = Array.from(window.crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
    const redirect_uri = 'https://tcas-student-api-thaid-dot-tcas65.as.r.appspot.com/thaid/callback';
    const url = `${config.thaid.host}/api/v2/oauth2/auth/?response_type=code&client_id=${config.thaid.clientId}&redirect_uri=${redirect_uri}&scope=pid&state=${state}`;
    window.location.href = url;
  }

  const [socialLogin, setSocialLogin] = useState();
  const _onLookup = async () => {
    if (!citizenIdType) return setWrongCitizenId(true);
    if (lookingUp || !citizenId) return;
    try {
      setLookingUp(true);
      const result = await callAPI({
        method: 'POST',
        url: '/applicants/student-lookup',
        body: { citizenId },
      });

      setLookupState(result.state);
      setSocialLogin(result.social_login);
      setAuthCookie('sessionToken', result.token, 1 / 24);
      if (['obec', 'obec-past', 'ticket'].includes(result.state)) {
        if (registrable) {
          navigate('/form');
        }
      } else if (result.state === 'tutor-suspected') {
        navigate(`/veto/${result.blacklist_token}`);
      }

      setLookingUp(false);
    } catch (error) {
      console.error(new Error(`Error while trying to lookup by citizenId: ${error.message}`));
      showErrorToast(userErrorMessage(error));
      setLookingUp(false);
    }
  };

  const [loggingIn, setLoggingIn] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [loginFail, setLoginFail] = useState(false);
  const SESSION_EXPIRED_MESSAGE = 'หมดเวลาทำรายการ ระบบเริ่มรายการใหม่ให้แล้ว กรุณากดเข้าสู่ระบบอีกครั้ง';
  const [loginErrMsg, setLoginErrMsg] = useState();

  const _onTCASLogin = async () => {
    if (loggingIn) return;
    try {
      setLoggingIn(true);
      setLoginErrMsg();
      setLoginFail(false);
      const { access_token } = await callAPI({
        method: 'POST',
        url: '/tcas-past/login',
        body: {
          citizenId,
          password,
        },
      });
      setAuthCookie('accessToken', access_token, 1 / 24);
      setLoggingIn(false);
      setLoginFail(false);
      window.location.href = '/profile/applicant-info';
      // navigate('/profile/applicant-info');
    } catch (error) {
      console.error(new Error(`Error while trying to log in using TCAS account: ${error.message}`));
      setLoginErrMsg(_.get(error, 'json.errors.0.msg'));
      setLoggingIn(false);
      setModalVisible(false);
      setLoginFail(true);
    }
  };

  const _onPasswordLogin = async () => {
    if (loggingIn) return;
    try {
      setLoggingIn(true);
      setLoginErrMsg();
      setLoginFail(false);
      const { access_token } = await callAPI({
        method: 'POST',
        url: '/applicants/login-with-password',
        body: {
          citizenId,
          password,
        },
      });
      setAuthCookie('accessToken', access_token, 1 / 24);
      Cookies.remove('email_timer_ends_at');
      Cookies.remove('mobile_timer_ends_at');
      // navigate('/profile');
      window.location.href = '/profile';
      setLoggingIn(false);
      setLoginFail(false);
    } catch (error) {
      console.error(new Error(`Error while trying to log in: ${error.message}`));
      setLoggingIn(false);
      setLoginFail(true);
      // G29-5: session จาก student-lookup หมดอายุ (1 ชม.) → 401 unauthorized — เดิมขึ้น "รหัสผ่านไม่ถูกต้อง" ทุกครั้งที่ลองใหม่
      // ต่อ session ให้ (lookup ใหม่) แล้วให้กดเข้าสู่ระบบอีกครั้ง
      if (error.statusCode === 401 && _.get(error, 'json.errorCode') === 'unauthorized') {
        setLoginErrMsg(SESSION_EXPIRED_MESSAGE);
        _onLookup();
        return;
      }
      setLoginErrMsg(_.get(error, 'json.errors.0.msg'));
    }
  };

  const _onLoginClick = () => {
    // if (!consentAccepted) return;
    // Enter ทั้งช่องเลขประจำตัวและช่องรหัสผ่านเรียกตรงนี้ตรง ๆ — กันที่ปุ่มอย่างเดียวไม่พอ
    // (รหัสผ่านว่างแล้วยิง API → backend ตอบ "Invalid value" ของ express-validator)
    if (loginDisabled) return;
    if (['tcas-past', 'tcas'].includes(lookupState)) {
      setModalVisible(true);
    } else if (lookupState === 'registered' && (loginMethod === 'password' || !socialLogin)) {
      _onPasswordLogin();
    } else {
      _onLookup();
    }
  };

  const [fbLoggingIn, setFbLoggingIn] = useState(false);
  const _onFacebookLogin = async (accessToken) => {
    if (fbLoggingIn) return;
    try {
      setFbLoggingIn(true);
      const { access_token } = await callAPI({
        method: 'POST',
        url: '/socials/login-with-facebook',
        body: { accessToken },
      });
      setAuthCookie('accessToken', access_token, 1 / 24);
      Cookies.remove('email_timer_ends_at');
      Cookies.remove('mobile_timer_ends_at');
      // navigate('/profile');
      window.location.href = '/profile';
      setFbLoggingIn(false);
    } catch (error) {
      const errorMessage = error.message;
      showErrorToast(userErrorMessage(error));
      console.error(new Error(`Error while trying to login with facebook: ${errorMessage}`));
      setFbLoggingIn(false);
    }
  };

  const [ggLoggingIn, setGgLoggingIn] = useState(false);
  const _onGoogleLogin = async (accessToken) => {
    if (ggLoggingIn) return;
    try {
      setGgLoggingIn(true);
      const { access_token } = await callAPI({
        method: 'POST',
        url: '/socials/login-with-google',
        body: { accessToken },
      });
      setAuthCookie('accessToken', access_token, 1 / 24);
      Cookies.remove('email_timer_ends_at');
      Cookies.remove('mobile_timer_ends_at');
      // navigate('/profile');
      window.location.href = '/profile';
      setGgLoggingIn(false);
    } catch (error) {
      const errorMessage = error.message;
      showErrorToast(userErrorMessage(error));
      console.error(new Error(`Error while trying to login with google: ${errorMessage}`));
      setGgLoggingIn(false);
    }
  };

  const onPasswordChange = (e) => {
    const value = `${e.target.value || ''}`;
    if (value.includes(' ')) return;
    setPassword(value);
  };

  // const _onConsentAccept = () => {
  //   Cookies.set('consent_accepted_at', new Date().getTime(), { expires: 365 });
  //   setConsentAccepted(true);
  // };

  // ช่องรหัสผ่านโผล่เมื่อไหร่ = ต้องกรอกก่อนเข้าสู่ระบบเสมอ (เงื่อนไขเดียวกับที่ render ช่องนั้น)
  const passwordRequired =
    lookupState === 'tcas-past' ||
    lookupState === 'tcas' ||
    (lookupState === 'registered' && (loginMethod === 'password' || !socialLogin));

  const loginDisabled =
    !citizenId.length ||
    lookupState === 'not-found' ||
    (passwordRequired && !password);

  return (
    <div className="t-form">
      {!ticketSuccessEmail && (
        <>
          <h1>
            {'ลงทะเบียน'} • {'เข้าสู่ระบบ'}
          </h1>
          <p className="text-center">
            <img src="/assets/img/thai_id.svg" width="213" height="122" alt="Thai ID Card" />
          </p>
          {!lookupState && thaidError ? (
            <div className="t-box -error -half">
              <h2>{'เข้าสู่ระบบด้วย ThaID ไม่สำเร็จ'}</h2>
              {'กรุณาลองใหม่อีกครั้ง หรือกรอกเลขประจำตัวประชาชนด้านล่างเพื่อเข้าสู่ระบบ'}
            </div>
          ) : !lookupState && wrongCitizenId ? (
            <div className="t-box -error -half">
              <h2>{'ผิดพลาด'}</h2>
              {'รูปแบบเลขประจำตัวของท่านไม่ถูกต้อง กรุณาตรวจสอบใหม่อีกครั้ง'}
            </div>
          ) : lookupState === 'not-found' ? (
            <div className="t-box -error -half">
              <h2>{'ผิดพลาด'}</h2>
              <div>
                {'กรุณาตรวจสอบหมายเลขของท่านอีกครั้ง หากไม่พบข้อมูลของท่าน กรุณาแจ้งข้อมูลเพิ่มเติมเพื่อทำการตรวจสอบโดยละเอียด'}
              </div>
            </div>
          ) : (lookupState && lookupState !== 'registered' && !registrable) ? (
            <div className="t-box -error -half">
              <h2>{'ปิดระบบลงทะเบียน'}</h2>
              {'ท่านไม่ได้ลงทะเบียนในระยะเวลาที่กำหนด'}{' '}
            </div>
          ) : (
            lookupState !== 'registered' &&
            lookupState !== 'tcas-past' &&
            lookupState !== 'tcas' && (
              <div className="t-box -info -half">
                {'เข้าระบบลงทะเบียนด้วย'} <b>{'เลขประจำตัวประชาชน 13 หลัก'}</b> {'หากไม่มี ให้ใช้'}{' '}
                <b>G Number</b> {'หรือ'}
                <b> Passport Number</b>
                {/* <div style={{ lineHeight: '16px' }}>
                  <small className="color-orange">
                    <br />
                    คำแนะนำ: โปรดศึกษารายละเอียดและรูปแบบการให้คะแนนของแบบทดสอบทาง
                    {' '}
                    <a target="_blank" href="https://www.mytcas.com/blueprint"><u>blueprint.mytcas.com</u></a>
                    {' '}
                    โดยเฉพาะวิชา TGAT ตอนที่ 3 ส่วนที่ 1 การสร้างคุณค่าและนวัตกรรม
                    <br />
                    <br />
                    ก่อนทำข้อสอบโปรดอ่านคำสั่งและการให้คะแนนแต่ละส่วนแต่ละข้อให้ละเอียด
                  </small>
                </div> */}
              </div>
            )
          )}
          {lookupState === 'banned' && (
            <div className="t-box -error -half">
              <h2>{'ถูกระงับ'}</h2>
              {'บัญชีนี้ถูกระงับการใช้งาน หากมีข้อสงสัย กรุณาติดต่อทปอ.'}{' '}
              <a href="https://fb.me/CUPTmytcas">fb.me/CUPTmytcas</a>
            </div>
          )}
          {loginFail && (
            <div className="t-box -error -half">
              <h2>{'ผิดพลาด'}</h2>
              {loginErrMsg || 'รหัสผ่านไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง'}
            </div>
          )}
          <div className="t-row">
            <div
              className={classNames('col required', {
                valid: lookupState && lookupState !== 'not-found',
                invalid: lookupState === 'not-found',
              })}
            >
              <label className="has-meta">
                <span>{'เลขประจำตัว'}</span>
                {citizenIdType === 'citizen_id' && <span className="meta"> ({citizenTypeLabel}) </span>}
              </label>
              <input
                type="text"
                className={classNames({ invalid: lookupState === 'not-found' })}
                required
                value={citizenId}
                onChange={_onCitizenIdChange}
                onKeyPress={_onCitizenIdEnter}
              />
            </div>
            {(lookupState === 'tcas-past' ||
              lookupState === 'tcas' ||
              (lookupState === 'registered' && (loginMethod === 'password' || !socialLogin))) && (
                <div className="t-row">
                  <div className="col required">
                    <label>{'รหัสผ่าน'}</label>
                    <input
                      type={passwordHidden ? 'password' : 'text'}
                      placeholder={'กรอกรหัสผ่าน'}
                      value={password}
                      onChange={onPasswordChange}
                      onKeyPress={_onPasswordEnter}
                    />
                    <i
                      className={classNames('i-viewpass cursor-pointer', { active: !passwordHidden })}
                      onClick={onPasswordHiddenChange}
                    />
                  </div>
                </div>
              )}
          </div>
          {lookupState !== 'registered' ||
            (lookupState === 'registered' && (loginMethod === 'password' || !socialLogin)) ? (
            <div className="action">
              <p>
                <a
                  className={classNames('btn-main cursor-pointer', { disabled: loginDisabled })}
                  onClick={loginDisabled ? () => { } : _onLoginClick}
                >
                  {'เข้าสู่ระบบ'}
                </a>
              </p>
              <p>
                <a className={classNames('btn-thaid cursor-pointer')} onClick={_onThaidLogin}>
                  <img alt="ThaiD" src="/assets/img/thaid.svg" width="32" height="32" />
                  <span>{'ดำเนินการต่อด้วย ThaiD'}</span>
                </a>
              </p>
            </div>
          ) : (
            <p className="action">
              <div className="text-center">
                {socialLogin === 'facebook' ? (
                  <FacebookLogin onAuthenticate={_onFacebookLogin} />
                ) : (
                  socialLogin === 'google' && <GoogleLogin onAuthenticate={_onGoogleLogin} />
                )}
                {socialLogin && loginMethod === 'social' && (
                  <div>
                    <p className="t-or">
                      <span className="or">{'หรือ'}</span>
                    </p>
                    <p>
                      <a className="_heading cursor-pointer" onClick={() => setLoginMethod('password')}>
                        {'เข้าสู่ระบบด้วยรหัสผ่าน?'}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </p>
          )}

          {(lookupState === 'tcas-past' ||
            lookupState === 'tcas' ||
            (lookupState === 'registered' && (loginMethod === 'password' || !socialLogin))) && (
              <div className="text-center _heading">
                <Link to={`/${lookupState}/forgot-password`}>{'ลืมรหัสผ่าน?'}</Link>
                {loginMethod === 'password' && (
                  <span>
                    &nbsp;{'หรือ'}{' '}
                    <a className="cursor-pointer" onClick={() => setLoginMethod('social')}>
                      {'เข้าสู่ระบบด้วยโซเชียล'}
                    </a>
                  </span>
                )}
              </div>
            )}
        </>
      )
      }
      {
        registrable && !ticketSuccessEmail && lookupState === 'not-found' && (
          <details>
            <summary>{'แจ้งข้อมูลเพิ่มเติม'}</summary>
            <div className="t-panel">
              <NotFoundTicket
                citizenId={citizenId}
                citizenTypeLabel={citizenTypeLabel}
                onSubmitSuccess={(email) => setTicketSuccessEmail(email)}
              />
            </div>
          </details>
        )
      }
      {Boolean(ticketSuccessEmail) && <NotFoundSubmitted email={ticketSuccessEmail} />}
      <ModalTermsAndConditions visible={modalVisible} onSubmit={_onTCASLogin} onClose={() => setModalVisible(false)} />
      {/* <ModalPDPAConsent visible={!consentAccepted} onSubmit={_onConsentAccept} /> */}
    </div >
  );
};

export default Lookup;
